import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, Globe, MessageSquare, Scale } from 'lucide-react';

const AISettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLanguage, setAiLanguage] = useState('en-US');
  const [aiTone, setAiTone] = useState('professional');
  const [aiFormality, setAiFormality] = useState('balanced');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setAiLanguage(response.data.ai_language || 'en-US');
      setAiTone(response.data.ai_tone || 'professional');
      setAiFormality(response.data.ai_formality || 'balanced');
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.put('/settings', {
        ai_language: aiLanguage,
        ai_tone: aiTone,
        ai_formality: aiFormality
      });
      toast.success('AI settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save AI settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 max-w-4xl mx-auto">
        <div className="text-center py-12">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/settings')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Settings
        </Button>
      </div>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-purple-600" />
          AI Settings
        </h1>
        <p className="text-slate-600 mt-2">
          Configure AI language, tone, and behavior for all AI-powered features
        </p>
      </div>

      <form onSubmit={handleSave}>
        <Card>
          <CardHeader>
            <CardTitle>AI Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Language/Dictionary */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-slate-500" />
                <span>Language & Dictionary</span>
              </Label>
              <Select value={aiLanguage} onValueChange={setAiLanguage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en-US">
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">English (US)</span>
                      <span className="text-xs text-slate-500">American spelling: color, organization</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="en-GB">
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">English (UK)</span>
                      <span className="text-xs text-slate-500">British spelling: colour, organisation</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="en-AU">
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">English (Australia)</span>
                      <span className="text-xs text-slate-500">Australian English spelling</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="en-CA">
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">English (Canada)</span>
                      <span className="text-xs text-slate-500">Canadian English spelling</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Affects spelling, grammar, and vocabulary in AI-generated content
              </p>
            </div>

            {/* Tone */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-slate-500" />
                <span>Communication Tone</span>
              </Label>
              <Select value={aiTone} onValueChange={setAiTone}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">Professional</span>
                      <span className="text-xs text-slate-500">Business-appropriate and polished</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="friendly">
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">Friendly</span>
                      <span className="text-xs text-slate-500">Warm and approachable</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="casual">
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">Casual</span>
                      <span className="text-xs text-slate-500">Relaxed and conversational</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="formal">
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">Formal</span>
                      <span className="text-xs text-slate-500">Traditional and respectful</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Sets the overall style of AI-generated communications
              </p>
            </div>

            {/* Formality */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Scale className="w-4 h-4 text-slate-500" />
                <span>Formality Level</span>
              </Label>
              <Select value={aiFormality} onValueChange={setAiFormality}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relaxed">
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">Relaxed</span>
                      <span className="text-xs text-slate-500">Less formal, more conversational</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="balanced">
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">Balanced</span>
                      <span className="text-xs text-slate-500">Mix of formal and casual elements</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="formal">
                    <div className="flex flex-col items-start">
                      <span className="font-semibold">Formal</span>
                      <span className="text-xs text-slate-500">Traditional business language</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Controls the level of formality in language and structure
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
              <h4 className="font-semibold text-blue-900 mb-2 text-sm">Where these settings apply:</h4>
              <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                <li>Email composition AI analysis</li>
                <li>Future AI features (document generation, templates, etc.)</li>
                <li>All AI-powered content creation tools</li>
              </ul>
            </div>

            <Button
              type="submit"
              disabled={saving}
              className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-md"
            >
              {saving ? 'Saving...' : 'Save AI Settings'}
            </Button>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default AISettings;
